/**
 * MaterializeWizard — flux guidé multi-step pour matérialisation enrichie
 * (Chunk 9.T.5.v3.6).
 *
 * Remplace le legacy `MaterializeEventDialog.tsx` (914 LOC monolithique)
 * conformément Q1/Q3 PO 2026-07-14. Consomme `MaterializeIntakeEventSchema`
 * v3.5 avec `review_snapshot` optionnel.
 *
 * 6 steps (design §5.4 + PO Q7 v3.6) :
 *   1. Direction du document (doc_direction)
 *   2. Client (strategy company)
 *   3. Projet (strategy project)
 *   4. Données extraites (apply_project_data + apply_company_data)
 *   5. Assureurs ciblés (create_quote_request optionnel)
 *   6. Résumé + confirmed_create + submit
 *
 * Draft persistence : `materializeDraftStorage` scellé par `intake_event_id`
 * (QA §4 v3.6 PO). Cleanup post-succès / post-rejet / event change / schema mismatch.
 *
 * Anti-régression : ce composant remplace `<MaterializeEventDialog>` dans
 * `IntakeEventDetailPage`. Le legacy fichier est supprimé (Q1 refacto propre).
 */

import type {
  ClassificationPayload,
  DocDirection,
  MaterializeIntakeEventInput,
  MaterializeStrategy,
} from "@kollectivia/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { StepIndicator } from "../../../../components/StepIndicator";
import { ApiException } from "../../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../../lib/apiErrorMessage";
import {
  clearMaterializeDraft,
  isMaterializeDraftDirty,
  loadMaterializeDraft,
  saveMaterializeDraft,
  type MaterializeDraftData,
} from "../../../../lib/materializeDraftStorage";
import { useFormResponse } from "../../../../lib/queries/formResponses";
import { useMaterializeIntakeEvent } from "../../../../lib/queries/intakeEvents";
import { toast } from "../../../../lib/toast";

import { buildMaterializePrefill } from "./materializePrefill";
import { Step1DocDirection } from "./Step1DocDirection";
import { Step2Company } from "./Step2Company";
import { Step3Project } from "./Step3Project";
import { Step4ExtractedData } from "./Step4ExtractedData";
import { Step5TargetInsurers } from "./Step5TargetInsurers";
import { Step6Summary } from "./Step6Summary";

type StepNum = 1 | 2 | 3 | 4 | 5 | 6;

export type WizardData = MaterializeDraftData;

type Props = {
  readonly open: boolean;
  readonly eventId: string | null;
  readonly classification: ClassificationPayload | null | undefined;
  /**
   * 1e-B — id de la form_response d'origine (si l'event vient d'une saisie
   * directe conseiller). Sert à préremplir best-effort le wizard par `field.role`.
   * `null`/absent → aucun prefill (comportement historique).
   */
  readonly formResponseId?: string | null;
  readonly onClose: () => void;
  readonly onMaterialized: () => void;
};

const EMPTY: WizardData = {};

/**
 * Extrait la direction suggérée depuis la classification IA — QA §1 :
 * pré-selection UNIQUEMENT si suggestion réelle disponible (pas de
 * dérivation implicite depuis classification.kind sans contrat).
 * Le schema `ClassificationPayload` v3.2 pourra exposer `suggested_doc_direction`
 * (dette v3.2 future). Aujourd'hui : lecture optionnelle si le champ existe.
 */
function extractSuggestedDocDirection(
  cls: ClassificationPayload | null | undefined,
): DocDirection | null {
  if (!cls) return null;
  const maybe = (cls as unknown as { suggested_doc_direction?: DocDirection })
    .suggested_doc_direction;
  if (
    maybe === "advisor_document" ||
    maybe === "insurer_response" ||
    maybe === "client_input" ||
    maybe === "unknown"
  ) {
    return maybe;
  }
  return null;
}

export function MaterializeWizard(props: Props) {
  const { open, eventId, classification, formResponseId, onClose, onMaterialized } = props;
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const qc = useQueryClient();
  const materialize = useMaterializeIntakeEvent();
  const dialogTitleId = useId();
  const dialogDescId = useId();

  const [phase, setPhase] = useState<"checking" | "resume" | "active">("checking");
  const [step, setStep] = useState<StepNum>(1);
  const [data, setData] = useState<WizardData>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const firstFocusRef = useRef<HTMLButtonElement | null>(null);
  // One-shot de décision par ouverture : garantit que le seed prefill / la
  // détection resume ne s'appliquent qu'UNE fois par ouverture, jamais par-dessus
  // une saisie déjà faite (invariant PO : ne jamais écraser).
  const seededRef = useRef(false);

  const suggestedDocDirection = useMemo(
    () => extractSuggestedDocDirection(classification),
    [classification],
  );

  // 1e-B — prefill best-effort depuis la form_response d'origine (si présente).
  // Le wizard NE dépend PAS d'un cache préchauffé : on attend la résolution de la
  // query pendant le fresh-start (succès → seed ; 404/réseau → wizard vide + avertissement).
  const responseQ = useFormResponse(formResponseId ?? undefined);
  const prefill = useMemo<Partial<MaterializeDraftData>>(
    () =>
      responseQ.data
        ? buildMaterializePrefill(responseQ.data.response.data, responseQ.data.version.schema)
        : {},
    [responseQ.data],
  );
  const prefillSettled = !formResponseId || responseQ.isSuccess || responseQ.isError;
  const prefillFailed = !!formResponseId && responseQ.isError;

  // Reset du one-shot à la fermeture → une réouverture re-décide (et re-seed).
  useEffect(() => {
    if (!open) seededRef.current = false;
  }, [open]);

  // Décision au mount (par event), APRÈS résolution du prefill. One-shot via seededRef.
  useEffect(() => {
    if (!open || !eventId) return;
    if (!prefillSettled) return; // rester en "checking" jusqu'à la résolution
    if (seededRef.current) return; // décision déjà prise pour cette ouverture
    seededRef.current = true;
    const draft = loadMaterializeDraft(eventId);
    if (draft && isMaterializeDraftDirty(draft.data)) {
      // Brouillon dirty existant → JAMAIS écrasé : Resume/Discard proposés.
      setPhase("resume");
      return;
    }
    // Fresh start (aucun brouillon dirty) → seed prefill one-shot best-effort.
    setPhase("active");
    setStep(1);
    setData(prefill);
  }, [open, eventId, prefillSettled, prefill]);

  // Persistance à chaque changement (post-active).
  useEffect(() => {
    if (phase !== "active" || !eventId) return;
    if (!isMaterializeDraftDirty(data)) return;
    saveMaterializeDraft(eventId, step, data);
  }, [data, step, phase, eventId]);

  // Esc keydown close (parity dialog A11y).
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !materialize.isPending) {
        onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose, materialize.isPending]);

  const update = useCallback(
    (patch: Partial<WizardData>) => setData((prev) => ({ ...prev, ...patch })),
    [],
  );

  const onResume = () => {
    if (!eventId) return;
    const draft = loadMaterializeDraft(eventId);
    if (draft) {
      setData(draft.data);
      setStep(draft.currentStep as StepNum);
    }
    setPhase("active");
  };

  const onDiscard = () => {
    if (eventId) clearMaterializeDraft(eventId);
    // 1e-B — après « Abandonner », on repart d'un fresh-start PRÉ-REMPLI depuis
    // la form_response (seed one-shot déclenché par ce geste ; les modifications
    // faites ensuite ne sont plus écrasées).
    setData(prefill);
    setStep(1);
    setPhase("active");
  };

  const onCancel = () => {
    if (eventId) clearMaterializeDraft(eventId);
    setData(EMPTY);
    setStep(1);
    setError(null);
    onClose();
  };

  // ---------------------------------------------------------------------------
  // Validation par step (QA §5 v3.6 : combinaisons invalides bloquées).
  // ---------------------------------------------------------------------------

  const canGoNext = useMemo((): boolean => {
    switch (step) {
      case 1:
        return !!data.docDirection;
      case 2:
        if (!data.strategy) return false;
        if (data.strategy === "attach_existing_project") return true; // company vient du project
        if (data.strategy === "attach_company_create_project") return !!data.existingCompanyId;
        if (data.strategy === "create_company_and_project")
          return !!data.newCompany?.legal_name?.trim();
        return false;
      case 3:
        if (data.strategy === "attach_existing_project") return !!data.existingProjectId;
        // 9.T.5.v3.6 QA #3 — insurer_codes facultatifs à l'étape Projet.
        // Un document peut être un ajout au dossier, une pièce justificative,
        // etc. Les assureurs deviennent obligatoires uniquement à l'étape 5
        // lorsque le toggle `create_quote_request` est activé.
        return !!data.newProject?.title?.trim();
      case 4:
        // Step 4 est optionnel — pas de validation bloquante.
        return true;
      case 5: {
        // Toggle OFF autorisé (aucune QR). Si toggle ON : target_insurers non-vide + unique.
        if (!data.createQuoteRequestEnabled) return true;
        const targets = data.createQuoteRequest?.target_insurers ?? [];
        if (targets.length === 0) return false;
        if (new Set(targets).size !== targets.length) return false;
        return true;
      }
      case 6:
        // Confirmed_create requis pour stratégies (b) et (c). Non requis pour (a).
        if (data.strategy === "attach_existing_project") return true;
        return !!data.confirmedCreate;
    }
  }, [step, data]);

  const goNext = () => {
    if (!canGoNext) {
      setError(t("intake_events.detail.materialize.wizard.validation.cant_continue"));
      return;
    }
    setError(null);
    if (step < 6) setStep((s) => (s + 1) as StepNum);
  };
  const goBack = () => {
    setError(null);
    if (step > 1) setStep((s) => (s - 1) as StepNum);
  };

  // ---------------------------------------------------------------------------
  // Build body → mutate submit (Step 6)
  // ---------------------------------------------------------------------------

  const buildBody = useCallback((): MaterializeIntakeEventInput | null => {
    if (!data.strategy || !data.docDirection) return null;

    const body: MaterializeIntakeEventInput = {
      strategy: data.strategy,
      confirmed_create: !!data.confirmedCreate,
    };

    // Strategy-specific fields
    if (data.strategy === "attach_existing_project" && data.existingProjectId) {
      body.existing_project_id = data.existingProjectId;
    }
    if (data.strategy === "attach_company_create_project") {
      if (data.existingCompanyId) body.existing_company_id = data.existingCompanyId;
      // 9.T.5.v3.6 QA #3 — insurer_codes facultatif (default []). Le body
      // reste valide backend v3.5 (MaterializeNewProjectSchema override).
      if (data.newProject?.title) {
        body.new_project = {
          title: data.newProject.title,
          insurer_codes: (data.newProject.insurer_codes ?? []) as never,
          guarantee_codes: (data.newProject.guarantee_codes ?? []) as never,
          tags: [],
        };
      }
    }
    if (data.strategy === "create_company_and_project") {
      if (data.newCompany?.legal_name) {
        body.new_company = {
          legal_name: data.newCompany.legal_name,
          language: (data.newCompany.language ?? "fr") as "fr" | "en",
        } as never;
      }
      if (data.newProject?.title) {
        body.new_project = {
          title: data.newProject.title,
          insurer_codes: (data.newProject.insurer_codes ?? []) as never,
          guarantee_codes: (data.newProject.guarantee_codes ?? []) as never,
          tags: [],
        };
      }
    }

    // v3.5 review_snapshot (optionnel — présent si data avancée saisie Step 4-5)
    const hasApplyProject =
      data.applyProjectData && Object.values(data.applyProjectData).some((v) => v !== undefined);
    const hasApplyCompany =
      data.applyCompanyData && Object.values(data.applyCompanyData).some((v) => v !== undefined);
    const hasCreateQR =
      data.createQuoteRequestEnabled && (data.createQuoteRequest?.target_insurers?.length ?? 0) > 0;

    if (hasApplyProject || hasApplyCompany || hasCreateQR || data.docDirection) {
      body.review_snapshot = {
        doc_direction: data.docDirection,
        ...(hasApplyProject ? { apply_project_data: data.applyProjectData as never } : {}),
        ...(hasApplyCompany ? { apply_company_data: data.applyCompanyData as never } : {}),
        ...(hasCreateQR
          ? {
              create_quote_request: {
                target_insurers: data.createQuoteRequest!.target_insurers as never,
                ...(data.createQuoteRequest?.title ? { title: data.createQuoteRequest.title } : {}),
                ...(data.createQuoteRequest?.expected_response_by
                  ? { expected_response_by: data.createQuoteRequest.expected_response_by }
                  : {}),
              },
            }
          : {}),
      };
    }

    return body;
  }, [data]);

  const onSubmit = async () => {
    if (!eventId) return;
    const body = buildBody();
    if (!body) {
      setError(t("intake_events.detail.materialize.wizard.validation.cant_continue"));
      return;
    }
    setError(null);
    try {
      await materialize.mutateAsync({ eventId, input: body });
      // Cleanup post-succès (QA §4 v3.6)
      clearMaterializeDraft(eventId);
      toast.success(t("intake_events.detail.materialize.wizard.actions.submit"));
      // Invalidate parent queries
      void qc.invalidateQueries({ queryKey: ["intake-events", "detail", eventId] });
      onMaterialized();
    } catch (err) {
      if (err instanceof ApiException) {
        setError(getApiErrorMessage(err, lang) || t("errors.generic"));
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t("errors.generic"));
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!open || !eventId) return null;

  const stepLabels: string[] = [1, 2, 3, 4, 5, 6].map((n) =>
    t(`intake_events.detail.materialize.wizard.step_labels.${n}`),
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={dialogTitleId}
      aria-describedby={dialogDescId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
      onClick={(e) => {
        // 9.T.5.v3.6 QA #5 — backdrop close bloqué si draft dirty pour éviter
        // fermeture silencieuse pouvant faire croire au broker que tout est
        // perdu. Le draft est conservé sessionStorage mais le UX doit rendre
        // ça explicite. Broker doit cliquer Annuler (cleanup) OU Esc (idem).
        if (e.target !== e.currentTarget) return;
        if (materialize.isPending) return;
        if (isMaterializeDraftDirty(data)) return;
        onCancel();
      }}
    >
      <div
        className="max-h-[92vh] w-full min-w-0 max-w-3xl overflow-y-auto overflow-x-hidden rounded-xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-4">
          <h2 id={dialogTitleId} className="text-xl font-semibold text-slate-900">
            {t("intake_events.detail.materialize.wizard.title")}
          </h2>
          <p id={dialogDescId} className="sr-only">
            {t("intake_events.detail.materialize.wizard.title")}
          </p>
        </header>

        {phase === "checking" && (
          <p role="status" aria-live="polite" className="py-8 text-center text-sm text-slate-500">
            {t("intake_events.detail.materialize.wizard.checking")}
          </p>
        )}

        {phase === "resume" && (
          <ResumeInline
            titleKey="intake_events.detail.materialize.wizard.resume.title"
            bodyKey="intake_events.detail.materialize.wizard.resume.body"
            resumeCtaKey="intake_events.detail.materialize.wizard.resume.resume_cta"
            restartCtaKey="intake_events.detail.materialize.wizard.resume.restart_cta"
            onResume={onResume}
            onDiscard={onDiscard}
            firstFocusRef={firstFocusRef}
          />
        )}

        {phase === "active" && (
          <>
            {prefillFailed && (
              <div
                role="status"
                className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-800"
              >
                {t("intake_events.detail.materialize.wizard.prefill_unavailable")}
              </div>
            )}
            <div className="mb-6">
              <StepIndicator
                currentIndex={step - 1}
                steps={stepLabels}
                ariaLabel={t("intake_events.detail.materialize.wizard.title")}
                onStepClick={(idx) => {
                  if (materialize.isPending) return;
                  setError(null);
                  setStep((idx + 1) as StepNum);
                }}
                canGoToStep={(idx) => {
                  // Seulement les steps déjà complétés (idx < currentStep).
                  // Backward-safe : broker peut revenir sur Step 1/2/3 depuis
                  // Step 6 pour corriger sans casser les validations aval.
                  if (materialize.isPending) return false;
                  return idx < step - 1;
                }}
              />
            </div>

            {step === 1 && (
              <Step1DocDirection
                value={data.docDirection}
                onChange={(v) => update({ docDirection: v })}
                suggested={suggestedDocDirection}
              />
            )}
            {step === 2 && (
              <Step2Company data={data} onChange={update} classification={classification ?? null} />
            )}
            {step === 3 && (
              <Step3Project data={data} onChange={update} classification={classification ?? null} />
            )}
            {step === 4 && eventId && (
              <Step4ExtractedData intakeEventId={eventId} data={data} onChange={update} />
            )}
            {step === 5 && <Step5TargetInsurers data={data} onChange={update} />}
            {step === 6 && <Step6Summary data={data} onChange={update} />}

            {error && (
              <p
                role="alert"
                className="mt-4 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700"
              >
                {error}
              </p>
            )}

            <footer className="mt-6 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={materialize.isPending}
                className="btn-ghost focus-visible:ring-brand-500 focus-visible:ring-2"
              >
                {t("intake_events.detail.materialize.wizard.actions.cancel")}
              </button>
              <div className="flex items-center gap-2">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={goBack}
                    disabled={materialize.isPending}
                    className="btn-ghost focus-visible:ring-brand-500 focus-visible:ring-2"
                  >
                    {t("intake_events.detail.materialize.wizard.actions.back")}
                  </button>
                )}
                {step < 6 && (
                  <button
                    type="button"
                    onClick={goNext}
                    disabled={!canGoNext || materialize.isPending}
                    className="btn-primary focus-visible:ring-brand-500 focus-visible:ring-2"
                  >
                    {t("intake_events.detail.materialize.wizard.actions.next")}
                  </button>
                )}
                {step === 6 && (
                  <button
                    type="button"
                    onClick={onSubmit}
                    disabled={!canGoNext || materialize.isPending}
                    className="btn-primary focus-visible:ring-brand-500 focus-visible:ring-2"
                  >
                    {materialize.isPending
                      ? t("intake_events.detail.materialize.wizard.actions.submitting")
                      : t("intake_events.detail.materialize.wizard.actions.submit")}
                  </button>
                )}
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ResumeInline — mini écran de reprise inline (parity ResumeDraftScreen mais
// sans écran plein — le wizard vit dans un dialog).
// -----------------------------------------------------------------------------

function ResumeInline({
  titleKey,
  bodyKey,
  resumeCtaKey,
  restartCtaKey,
  onResume,
  onDiscard,
  firstFocusRef,
}: {
  titleKey: string;
  bodyKey: string;
  resumeCtaKey: string;
  restartCtaKey: string;
  onResume: () => void;
  onDiscard: () => void;
  firstFocusRef: React.RefObject<HTMLButtonElement>;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    firstFocusRef.current?.focus();
  }, [firstFocusRef]);
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
      <p className="mb-1 text-sm font-medium text-amber-900">{t(titleKey)}</p>
      <p className="mb-3 text-sm text-amber-800">{t(bodyKey)}</p>
      <div className="flex items-center gap-2">
        <button
          ref={firstFocusRef}
          type="button"
          onClick={onResume}
          className="btn-primary focus-visible:ring-brand-500 focus-visible:ring-2"
        >
          {t(resumeCtaKey)}
        </button>
        <button
          type="button"
          onClick={onDiscard}
          className="btn-ghost focus-visible:ring-brand-500 focus-visible:ring-2"
        >
          {t(restartCtaKey)}
        </button>
      </div>
    </div>
  );
}

// Silence unused imports lint (utilisés pour typage).
export type { MaterializeStrategy };
